import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/hooks/trpc";
import type { TestTemplateAttributeId } from "@brewbase/db/types";

interface AddMeasurementData {
  name: string;
  default_unit?: string;
  test_template_attribute_id?: TestTemplateAttributeId | null;
}

function blankForm() {
  return {
    name: "",
    unit: "",
    templateId: "",
    attributeId: "",
  };
}

export function AddMeasurementDialog({
  open,
  onOpenChange,
  isPending,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  isPending: boolean;
  onAdd: (data: AddMeasurementData) => void;
}) {
  const [form, setForm] = useState(blankForm);

  const set = <K extends keyof ReturnType<typeof blankForm>>(
    key: K,
    value: ReturnType<typeof blankForm>[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const { data: templates = [] } = trpc.food.testTemplates.list.useQuery(undefined, {
    enabled: open,
  });

  const templateIdNum = form.templateId ? (Number(form.templateId) as import("@brewbase/db/types").TestTemplateId) : null;

  const { data: attributes = [] } = trpc.food.testTemplates.listAttributes.useQuery(
    templateIdNum!,
    { enabled: open && templateIdNum != null },
  );

  function handleOpenChange(v: boolean) {
    if (!v) setForm(blankForm);
    onOpenChange(v);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    onAdd({
      name: form.name.trim(),
      default_unit: form.unit.trim() || undefined,
      test_template_attribute_id: form.attributeId
        ? (Number(form.attributeId) as TestTemplateAttributeId)
        : null,
    });
  }

  const canSave = form.name.trim().length > 0 && form.attributeId.length > 0 && !isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Add Measurement</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label>
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Crude Protein"
              className="h-9"
              autoFocus
            />
          </div>

          {/* Unit */}
          <div className="space-y-1.5">
            <Label>Unit</Label>
            <Input
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              placeholder="e.g. %"
              className="h-9"
            />
          </div>

          {/* Divider */}
          <div className="border-t" />

          {/* Test template link */}
          <div className="space-y-3">
            <div className="space-y-0.5">
              <Label>Test template <span className="text-destructive">*</span></Label>
              <p className="text-xs text-muted-foreground">
                Link this measurement to a test template attribute so values are populated automatically during analysis.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Template</Label>
              <Select
                value={form.templateId}
                onValueChange={(v) => {
                  set("templateId", v);
                  set("attributeId", "");
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                      {t.category ? ` — ${t.category}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Attribute</Label>
              <Select
                value={form.attributeId}
                onValueChange={(v) => set("attributeId", v)}
                disabled={!form.templateId || attributes.length === 0}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={
                    !form.templateId
                      ? "Select a template first"
                      : attributes.length === 0
                        ? "No attributes found"
                        : "Select attribute…"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {attributes.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.attribute_name}
                      {a.attribute_unit ? ` (${a.attribute_unit})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Measurement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
